import PptxGenJS from "pptxgenjs";
import { getPresentationHtmlSlides } from "../html/templateEngine";
import { getPresentationPreset } from "../slides/slideData";
import type { PresentationLocale, PresentationPresetId } from "../slides/types";

type ExportPresentationDeckPptxParams = {
  preset: PresentationPresetId;
  locale: PresentationLocale;
  fileName?: string;
};

function waitForIframeLoad(iframe: HTMLIFrameElement) {
  return new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error("iframe timeout")), 20000);
    iframe.onload = () => {
      window.clearTimeout(timeout);
      resolve();
    };
  });
}

async function waitForIframeAssets(iframe: HTMLIFrameElement) {
  const doc = iframe.contentDocument;
  if (!doc) return;

  if (doc.fonts?.ready) {
    await doc.fonts.ready;
  }

  const images = Array.from(doc.querySelectorAll("img"));
  await Promise.all(
    images.map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        image.onload = () => resolve();
        image.onerror = () => resolve();
      });
    })
  );

  // Extra wait: CDN fonts (Google Fonts, Font Awesome) may still be loading
  // after doc.fonts.ready resolves - give them time to render CSS pseudo-elements.
  await new Promise<void>((resolve) => setTimeout(resolve, 1200));
}

function patchIframeDocForCapture(doc: Document) {
  // html2canvas doesn't support backdrop-filter - inject override style
  const patch = doc.createElement("style");
  patch.textContent = `
    * { backdrop-filter: none !important; -webkit-backdrop-filter: none !important; }
    .contact-card { background-color: rgba(26,26,26,0.95) !important; }
  `;
  doc.head?.appendChild(patch);
}

async function ensureFontsInMainDocument() {
  const FONT_HREFS = [
    "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600;700;800;900&family=JetBrains+Mono:wght@400;500&family=Open+Sans:wght@400;600&display=swap",
    "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.6.0/css/all.min.css",
  ];

  const added: HTMLLinkElement[] = [];
  for (const href of FONT_HREFS) {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
      added.push(link);
    }
  }

  if (added.length > 0) {
    // Wait for stylesheets to load
    await Promise.all(
      added.map(
        (link) =>
          new Promise<void>((resolve) => {
            link.onload = () => resolve();
            link.onerror = () => resolve();
          })
      )
    );
    // Give font files themselves time to download and register
    await new Promise<void>((resolve) => setTimeout(resolve, 1000));
  }

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
}

export async function exportPresentationDeckPptx({
  preset,
  locale,
  fileName,
}: ExportPresentationDeckPptxParams) {
  const selectedPreset = getPresentationPreset(preset);
  const outputName = fileName ?? selectedPreset.fileNameByLocale[locale].replace(/\.pdf$/i, ".pptx");
  const slidesHtml = await getPresentationHtmlSlides(locale, selectedPreset.slideIds);

  // Pre-load fonts in main document so html2canvas clones inherit them
  await ensureFontsInMainDocument();

  const { default: html2canvas } = await import("html2canvas");

  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";
  pptx.author = "Dario Marco Bellini";
  pptx.company = "Dimensione 4";
  pptx.subject = "Engineering presentation";
  pptx.title = "Dimensione 4 Deck";

  for (const slideHtml of slidesHtml) {
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.left = "-99999px";
    iframe.style.top = "0";
    iframe.style.width = "1280px";
    iframe.style.height = "720px";
    iframe.setAttribute("aria-hidden", "true");
    iframe.srcdoc = slideHtml;
    document.body.appendChild(iframe);

    try {
      await waitForIframeLoad(iframe);
      await waitForIframeAssets(iframe);

      const doc = iframe.contentDocument;
      if (!doc) continue;
      const root = (doc.querySelector(".slide-container") as HTMLElement) ?? doc.body;

      const canvas = await html2canvas(root, {
        backgroundColor: "#0a0a0a",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: 1280,
        height: 720,
        windowWidth: 1280,
        windowHeight: 720,
        onclone: (clonedDoc: Document) => {
          patchIframeDocForCapture(clonedDoc);
        },
      });

      const imageData = canvas.toDataURL("image/png", 1);
      const slide = pptx.addSlide();
      slide.addImage({ data: imageData, x: 0, y: 0, w: 13.333, h: 7.5 });
    } finally {
      iframe.remove();
    }
  }

  await pptx.writeFile({ fileName: outputName });
}
